// utils/stage.js
//
// Feb 2026 · UI stage normalizer.
//
// Some upstream asset rows have a `stage` / `phase` field that was
// hydrated from LLM-scraped text and ended up containing full
// descriptive sentences plus inline reference citations (e.g.
// "Phase 2 (NCT12345) — currently enrolling patients in China [1][2]").
// The Reference Library table cell is narrow, so rendering the raw
// value looks broken. This helper collapses the raw string down to a
// clean canonical stage label ("Phase 2", "Approved", "Preclinical",
// ...) that fits in a single cell.
//
// Rules (in order):
//   1. Strip bracketed reference citations: [1], [2,3], (ref: ...)
//   2. Match a canonical stage keyword and return that label
//   3. Otherwise, return the first short chunk (before any dash /
//      parenthesis / period), truncated to 30 chars
//
// Kept intentionally pure + framework-agnostic so both
// ViatrisReferenceLibraryPage and AssetsPage can share it.

const STAGE_PATTERNS = [
  // Ordered — more specific first so "Phase 2/3" beats "Phase 2"
  [/\bphase\s*(?:1\s*\/\s*2|i\s*\/\s*ii)\b/i, 'Phase 1/2'],
  [/\bphase\s*(?:2\s*\/\s*3|ii\s*\/\s*iii)\b/i, 'Phase 2/3'],
  [/\bphase\s*(?:3|iii)\b/i, 'Phase 3'],
  [/\bphase\s*(?:2|ii)\b/i, 'Phase 2'],
  [/\bphase\s*(?:1|i)\b/i, 'Phase 1'],
  [/\bphase\s*0\b/i, 'Phase 0'],
  [/\b(?:marketed|commercial(?:ized)?|launched)\b/i, 'Marketed'],
  [/\b(?:approved|registered)\b/i, 'Approved'],
  [/\bind\b/i, 'IND'],
  [/\b(?:nda|bla|filed|filing)\b/i, 'Filed'],
  [/\b(?:pre-?clinical|preclinical)\b/i, 'Preclinical'],
  [/\b(?:discovery|lead\s*optimization|research)\b/i, 'Discovery'],
];

export function normalizeStageLabel(raw) {
  if (raw == null) return '';
  let s = String(raw).trim();
  if (!s) return '';

  // Strip reference citations of common shapes.
  s = s
    .replace(/\[(?:\s*\d+\s*[,;-]?\s*)+\]/g, '') // [1], [2,3], [1-3]
    .replace(/\((?:ref|source|see)[^)]*\)/gi, '') // (ref: ...), (source ...)
    .replace(/\s{2,}/g, ' ')
    .trim();

  for (const [re, label] of STAGE_PATTERNS) {
    if (re.test(s)) return label;
  }

  // Fallback — take the first clause and hard-truncate.
  const firstClause = s.split(/[—–\-.(;\n]/)[0].trim();
  return firstClause.length > 30 ? `${firstClause.slice(0, 27)}…` : firstClause;
}

// Mirrors backend routes/stage_utils.is_preferred_stage_mismatch: a
// contact with declared preferred_stages only accepts assets whose
// canonical stage is in that set. Commercial / Marketed / Approved collapse.
const _stageKey = (raw) => {
  const label = normalizeStageLabel(raw);
  if (label === 'Marketed' || label === 'Approved') return 'approved';
  if (/^phase\s|^filed$|^ind$|^preclinical$|^discovery$/i.test(label)) return label.toLowerCase();
  return '';
};

export function stageGateBlocks(preferredStages, assetStage) {
  const prefs = (preferredStages || []).map(_stageKey).filter(Boolean);
  const asset = _stageKey(assetStage);
  if (!prefs.length || !asset) return false;
  return !prefs.includes(asset);
}
